/*
 * Copyright (c) 2024 Ask Sage, Inc. All rights reserved.
 * This software is the confidential and proprietary information of Ask Sage, Inc.
 * You shall not disclose such confidential information and shall use it only in
 * accordance with the terms of the license agreement you entered into with Ask Sage, Inc.
 * THIS SOFTWARE IS PROVIDED "AS IS" AND ANY EXPRESSED OR IMPLIED WARRANTIES,
 * INCLUDING, BUT NOT LIMITED TO, THE IMPLIED WARRANTIES OF MERCHANTABILITY AND
 * FITNESS FOR A PARTICULAR PURPOSE ARE DISCLAIMED. IN NO EVENT SHALL ASK SAGE, INC.
 * OR ITS CONTRIBUTORS BE LIABLE FOR ANY DIRECT, INDIRECT, INCIDENTAL, SPECIAL,
 * EXEMPLARY, OR CONSEQUENTIAL DAMAGES (INCLUDING, BUT NOT LIMITED TO, PROCUREMENT
 * OF SUBSTITUTE GOODS OR SERVICES; LOSS OF USE, DATA, OR PROFITS; OR BUSINESS
 * INTERRUPTION) HOWEVER CAUSED AND ON ANY THEORY OF LIABILITY, WHETHER IN CONTRACT,
 * STRICT LIABILITY, OR TORT (INCLUDING NEGLIGENCE OR OTHERWISE) ARISING IN ANY WAY
 * OUT OF THE USE OF THIS SOFTWARE, EVEN IF ADVISED OF THE POSSIBILITY OF SUCH DAMAGE.
 * You may not use this software except in compliance with the License.
 * You may not modify, reverse engineer, or redistribute this software.
 */
import './normal.css';
import { ErrorBoundary } from './ErrorBoundary'; 
import LoginForm from './UserManagement/LoginForm';
import RegisterForm from './UserManagement/RegisterForm';
import VerifyForm from './UserManagement/VerifyForm';
import ModifyForm from './UserManagement/ModifyForm';
import PaymentForm from './UserManagement/PaymentForm';
import UserLogs from './UserManagement/UserLogs';
import MFAForm from './UserManagement/MFAForm';
import LostPass1Form from './UserManagement/LostPass1Form';
import LostPass2Form from './UserManagement/LostPass2Form';
import SuperAdminUsersList from './SuperAdmin/SuperAdminUsersList';
import SuperAdminUsersLog from './SuperAdmin/SuperAdminUsersLog';
import SuperAdminStats from './SuperAdmin/SuperAdminStats';
import SuperAdminUserStats from './SuperAdmin/SuperAdminUserStats';
import SuperAdminTokenStats from './SuperAdmin/SuperAdminTokenStats';
import SuperAdminOrganizationsList from './SuperAdmin/SuperAdminOrganizationsList';
import SuperAdminOrganizationPurchasesList from './SuperAdmin/SuperAdminOrganizationPurchasesList';
import AdminUsersList from './Admin/AdminUsersList';
import AdminUserStats from './Admin/AdminUserStats';
import AdminTokenStats from './Admin/AdminTokenStats';
import AdminUsersLog from './Admin/AdminUsersLog';
import SAMLRedirect from './SAML/SAMLRedirect';
import SAMLLogin from './SAML/SAMLLogin';
import TermsPage from './Popups/TermsPage';
import PrivacyPage from './Popups/PrivacyPage';
import Home from './Chat/Home';
import CodeCanvas from './CodeCanvas/CodeCanvas';
import ProseCanvas from './ProseCanvas/ProseCanvas';
import ModelComparison from './Pages/ModelComparison/ModelComparison.js';
import WorkbookHome from './Workbook/Home.js';
import { BrowserRouter as Router, Route, Routes, Navigate } from 'react-router-dom';
import React from 'react';
import { LoggedRoute } from './Routes/LoggedRoute';
import { BotRoute } from './Routes/BotRoute';
import { SuperAdminLoggedRoute } from './Routes/SuperAdminLoggedRoute';
import { AdminLoggedRoute } from './Routes/AdminLoggedRoute';
import { NotLoggedRoute } from './Routes/NotLoggedRoute';
import { NotLoggedSAMLRoute } from './Routes/NotLoggedSAMLRoute';
import { getToken, logout, getWithExpiry, setWithExpiry } from './AuthService'
import { useEffect, useState } from 'react';
import { ThemeProvider } from '@mui/material';
import { darkTheme, lightTheme } from './themes';
import * as default_data from './config.js'
import log from 'loglevel';
import { useUserSettings } from './Contexts/UserPreferenceContext.js';

const LightThemeOverrider = ({children}) => {
  return (
    <ThemeProvider theme={lightTheme}>
      <div style={{ color: 'black' }} >
        {children}
      </div>
    </ThemeProvider>
  );
}

// Override the default logging method
log.methodFactory = function (methodName, logLevel, loggerName) {
  return function (...args) {
    // Send logs to stdout or stderr, depending on log level
    if (logLevel >= log.levels.ERROR) {
      console.error(`[${methodName}]`, ...args);
    } else {
      console.log(`[${methodName}]`, ...args);
    }

    try {
      // make args single string
      var log_entry = args.join(' ');
      // push log to server
      push_log(log_entry, logLevel);
    }
    catch(e) {
      console.log(e)
      return;
    }
  }
};

// Set the log level
log.setLevel(log.levels.DEBUG);

const push_log = (log_entry, log_level=3) => {
  
  try {
  var token = getToken()
  if(token === false) {
    return;
  }

  if(log_level !== 4) {
    log_level = 3;
  }
  return fetch(default_data.default_chat_service_url + '/log', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'x-access-tokens': `${token}`
    },
    body : JSON.stringify({
      log_entry: log_entry,
      log_level: log_level
    })
  })
    .then(response => {
      if (response.ok) {
        return response.json();
      }
      throw new Error('An error occurred while fetching organizations.');
    })
    .then(data => {
      if(data.status != 200) {
        throw new Error(data.response);
      }
      data = data.response;
    })
    .catch(error => {
      // if error contains Token is invalid then logout
      if(error.message.includes('Token is invalid')) {
        logout()
      }
    console.log(error);
    });
  }
  catch {
    return;
  }
}


function App() {
  const [currentColor, setCurrentColor] = useState('');
  const [currentDatasetMode, setCurrentDatasetMode] = useState('all');
  const {state} = useUserSettings();

  useEffect(() => {
    const handleDatasetModeChange = () => {
      if(getWithExpiry('currentDatasetMode') !== null && getWithExpiry('currentDatasetMode') === 'none') {
        setCurrentDatasetMode('none');
      }
      else {
        setCurrentDatasetMode('all');
      }      
    };

    const handleColorChange = () => {
      if(getWithExpiry('currentColor') !== null && getWithExpiry('currentColor') === 'white') {
        setCurrentColor('white');
      }
      else {
        setCurrentColor('black');
      }      
    };

    handleColorChange();
    handleDatasetModeChange();
  }, []);

  function switchColor() {
    if(currentColor === 'white') {
      setCurrentColor('black')
      setWithExpiry('currentColor', 'black', 1000 * 60 * 60 * 24 * 365 * 10)
    }
    else {
      setCurrentColor('white')
      setWithExpiry('currentColor', 'white', 1000 * 60 * 60 * 24 * 365 * 10)
    }
  }

  function switchDatasetMode() {
    if(currentDatasetMode === 'all') {
      setCurrentDatasetMode('none')
      setWithExpiry('currentDatasetMode', 'none', 1000 * 60 * 60 * 24 * 365 * 10)
    }
    else {
      setCurrentDatasetMode('all')
      setWithExpiry('currentDatasetMode', 'all', 1000 * 60 * 60 * 24 * 365 * 10)
    }
  }

  useEffect(() => {
    if(default_data.default_force_chat_redirect === true) {
      const currentUrl = window.location.href;
      const url = new URL(currentUrl);
      const hostname = url.hostname;
  
      // Extract the domain name by removing the first subdomain if it exists
      const parts = hostname.split('.');
      if (parts.length > 2) {
        parts.shift(); // Remove the first subdomain
      }
      const domainName = parts.join('.');
  
      // Redirect if the hostname does not start with 'chat.' or 'chat-nocac.'
      if (!hostname.startsWith('chat.') && !hostname.startsWith('chat-nocac.')) {
        const newHostname = `chat.${domainName}`;
        const newUrl = `https://${newHostname}`;
        window.location.replace(newUrl);
      }
    }
  }, []);

  useEffect(() => {
    import('./App.css');
  }, [currentColor]);

  if(window.RUNTIME_VARS && window.RUNTIME_VARS.REACT_APP_asksage_maintenance) {
    var maintenance = window.RUNTIME_VARS.REACT_APP_asksage_maintenance || false
    if(maintenance === 'true') {
      return (
        <div className="App">
          Sorry, Ask Sage is under maintenance for upgrades and should be back up as soon as possible.
        </div>
      )
    }
  }
  
    return (
      <div className="App">
        <Router>
          <ErrorBoundary>
            <Routes>
              <Route
                path="/"
                element={
                  <LoggedRoute>
                    <Home currentDatasetMode={currentDatasetMode} />
                  </LoggedRoute>
                }
              />
              <Route
                path="/bot"
                element={
                  <BotRoute>
                    <Home currentDatasetMode={currentDatasetMode} />
                  </BotRoute>
                }
              />
              <Route
                path="/dashboard"
                element={
                  <LoggedRoute>
                    <Home currentDatasetMode={currentDatasetMode} />
                  </LoggedRoute>
                }
              />
              <Route
                path="/codecanvas"
                element={
                  <LoggedRoute>
                    <CodeCanvas currentDatasetMode={currentDatasetMode} />
                  </LoggedRoute>
                }
              />
              <Route
                path="/prosecanvas"
                element={
                  <LoggedRoute>
                    <ProseCanvas currentDatasetMode={currentDatasetMode} />
                  </LoggedRoute>
                }
              />
              <Route
                path="/workbook"
                element={
                  <LoggedRoute>
                    <WorkbookHome />
                  </LoggedRoute>
                }
              />
              <Route
                path='/model-comparison'
                element={
                  <LoggedRoute>
                    <ModelComparison />
                  </LoggedRoute>
                }
              />
              <Route
                path="/payment"
                element={
                  <LoggedRoute>
                    <LightThemeOverrider>
                      <PaymentForm />
                    </LightThemeOverrider>
                  </LoggedRoute>
                }
              />
              <Route
                path="/update"
                element={
                  <LoggedRoute>
                    <LightThemeOverrider>
                      <ModifyForm switchDatasetMode={switchDatasetMode} currentDatasetMode={currentDatasetMode} switchColor={switchColor} currentColor={currentColor} />
                    </LightThemeOverrider>
                  </LoggedRoute>
                }
              />
              <Route
                path="/prompt-logs"
                element={
                  <LoggedRoute>
                    <LightThemeOverrider>
                      <UserLogs />
                    </LightThemeOverrider>
                  </LoggedRoute>
                }
              />
              <Route
                path="/mfa-setup"
                element={
                  <LoggedRoute>
                    <LightThemeOverrider>
                      <MFAForm />
                    </LightThemeOverrider>
                  </LoggedRoute>
                }
              />
              <Route
                path="/mfa-login"
                element={
                  <NotLoggedRoute>
                    <LightThemeOverrider>
                      <MFAForm login={true}/>
                    </LightThemeOverrider>
                  </NotLoggedRoute>
                }
              />
              <Route
                path="/register"
                element={
                  <NotLoggedRoute>
                    <LightThemeOverrider>
                      <RegisterForm />
                    </LightThemeOverrider>
                  </NotLoggedRoute>
                }
              />
              <Route
                path="/lost-pass"
                element={
                  <NotLoggedRoute>
                    <LightThemeOverrider>
                      <LostPass1Form />
                    </LightThemeOverrider>
                  </NotLoggedRoute>
                }
              />
              <Route
                path="/lost-pass-2"
                element={
                  <NotLoggedRoute>
                    <LightThemeOverrider>
                      <LostPass2Form />
                    </LightThemeOverrider>
                  </NotLoggedRoute>
                }
              />
              <Route
                path="/verify-email"
                element={
                  <NotLoggedRoute>
                    <LightThemeOverrider>
                      <VerifyForm />
                    </LightThemeOverrider>
                  </NotLoggedRoute>
                }
              />
              <Route
                path="/saml-redirect"
                element={
                  <NotLoggedSAMLRoute>
                    <LightThemeOverrider>
                      <SAMLRedirect />
                    </LightThemeOverrider>
                  </NotLoggedSAMLRoute>
                }
              />
              <Route
                path="/saml-login"
                element={
                  <NotLoggedSAMLRoute>
                    <LightThemeOverrider>
                      <SAMLLogin />
                    </LightThemeOverrider>
                  </NotLoggedSAMLRoute>
                }
              />
              <Route
                path="/superadmin/get-users"
                element={
                  <SuperAdminLoggedRoute>
                    <LightThemeOverrider>
                      <SuperAdminUsersList />
                    </LightThemeOverrider>
                  </SuperAdminLoggedRoute>
                }
              />
              <Route
                path="/superadmin/get-stats"
                element={
                  <SuperAdminLoggedRoute>
                    <LightThemeOverrider>
                      <SuperAdminStats />
                    </LightThemeOverrider>
                  </SuperAdminLoggedRoute>
                }
              />
              <Route
                path="/superadmin/get-user-stats"
                element={
                  <SuperAdminLoggedRoute>
                    <LightThemeOverrider>
                      <SuperAdminUserStats />
                    </LightThemeOverrider>
                  </SuperAdminLoggedRoute>
                }
              />
              <Route
                path="/superadmin/get-token-stats"
                element={
                  <SuperAdminLoggedRoute>
                    <LightThemeOverrider>
                      <SuperAdminTokenStats />
                    </LightThemeOverrider>
                  </SuperAdminLoggedRoute>
                }
              />
              <Route
                path="/superadmin/"
                element={
                  <SuperAdminLoggedRoute>
                    <LightThemeOverrider>
                      <SuperAdminUsersList />
                    </LightThemeOverrider>
                  </SuperAdminLoggedRoute>
                }
              />
              <Route
                path="/superadmin/get-organizations"
                element={
                  <SuperAdminLoggedRoute>
                    <LightThemeOverrider>
                      <SuperAdminOrganizationsList />
                    </LightThemeOverrider>
                  </SuperAdminLoggedRoute>
                }
              />
              <Route path="/superadmin/organization-purchase/:organization_id" element={
                  <SuperAdminLoggedRoute>
                    <LightThemeOverrider>
                      <SuperAdminOrganizationPurchasesList />
                    </LightThemeOverrider>
                  </SuperAdminLoggedRoute>
                }
              />
              <Route
                path="/superadmin/get-logs"
                element={
                  <SuperAdminLoggedRoute>
                    <LightThemeOverrider>
                      <SuperAdminUsersLog />
                    </LightThemeOverrider>
                  </SuperAdminLoggedRoute>
                }
              />
              <Route
                path="/admin/get-users"
                element={
                  <AdminLoggedRoute>
                    <LightThemeOverrider>
                      <AdminUsersList />
                    </LightThemeOverrider>
                  </AdminLoggedRoute>
                }
              />
              <Route
                path="/admin/get-user-stats"
                element={
                  <AdminLoggedRoute>
                    <LightThemeOverrider>
                      <AdminUserStats />
                    </LightThemeOverrider>
                  </AdminLoggedRoute>
                }
              />
              <Route
                path="/admin/get-token-stats"
                element={
                  <AdminLoggedRoute>
                    <LightThemeOverrider>
                      <AdminTokenStats />
                    </LightThemeOverrider>
                  </AdminLoggedRoute>
                }
              />
              <Route
                path="/admin/get-logs"
                element={
                  <AdminLoggedRoute>
                    <LightThemeOverrider>
                      <AdminUsersLog />
                    </LightThemeOverrider>
                  </AdminLoggedRoute>
                }
              />
              <Route
                path="/login"
                element={
                  <NotLoggedRoute>
                    <LightThemeOverrider>
                      <LoginForm />
                    </LightThemeOverrider>
                  </NotLoggedRoute>
                }
              />
              <Route path="/terms" element={ <LightThemeOverrider><TermsPage /></LightThemeOverrider> } />
              <Route path="/privacy" element={ <LightThemeOverrider><PrivacyPage /></LightThemeOverrider> } />
              <Route path="*" element={ <Navigate to="/register" /> } />
            </Routes>
          </ErrorBoundary>
        </Router>
     </div>
    )
}


export default App;
